import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@shared/auth/AuthContext';
import { FeatureGate } from '@shared/auth/FeatureGate';
import { Skeleton } from '@shared/ui';
import { NamedIcon } from '@shared/ui/icons';
import type { SocialView } from './BrowseSocialAnalytics';

const BrowseSocialAnalytics = lazy(() => import('./BrowseSocialAnalytics'));
const BrowseDigitalAnalytics = lazy(() => import('./BrowseDigitalAnalytics'));
const BrowseOutdoorAnalytics = lazy(() => import('./BrowseOutdoorAnalytics'));

// «Рынок»: first a choice of channel, then that channel's own screens — instead of
// three tabs stacked over one endless page.
const SECTIONS = [
  { key: 'social', feature: 'an_social', icon: 'social', to: '/customer/market/social' },
  { key: 'digital', feature: 'an_digital', icon: 'digital', to: '/customer/market/digital' },
  { key: 'outdoor', feature: 'an_outdoor', icon: 'outdoor', to: '/customer/market/outdoor' },
] as const;

function MarketHome() {
  const { t } = useTranslation('customer');
  const { hasFeature } = useAuth();
  const navigate = useNavigate();
  const allowed = SECTIONS.filter((s) => hasFeature(s.feature));
  if (allowed.length === 1) return <Navigate to={allowed[0].to} replace />;
  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      <h1 style={{ margin: '0 0 6px', fontSize: 'var(--fs-h1)', fontWeight: 'var(--fw-bold)' }}>{t('market.title')}</h1>
      <p style={{ margin: '0 0 16px', color: 'var(--muted)', fontSize: 'var(--fs-body)' }}>{t('market.lead')}</p>
      <div style={{ display: 'grid', gap: 10 }}>
        {allowed.map((s) => (
          <button key={s.key} type="button" onClick={() => navigate(s.to)}
            style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center', width: '100%', gap: 14, padding: '16px', minHeight: 76, textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit', color: 'inherit',
              background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14 }}>
            <NamedIcon name={s.icon} size={26} />
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', fontWeight: 600, fontSize: 'var(--fs-label)' }}>{t(`market.${s.key}.title`)}</span>
              <span style={{ display: 'block', fontSize: 'var(--fs-caption)', color: 'var(--muted)', marginTop: 2 }}>{t(`market.${s.key}.hint`)}</span>
            </span>
            <span aria-hidden="true" style={{ color: 'var(--accent)', fontSize: 20 }}>›</span>
          </button>
        ))}
      </div>
    </div>
  );
}

const VIEWS: SocialView[] = ['summary', 'brands', 'creators', 'sov'];

function MarketSocial() {
  // One mounted instance for all four screens (see SocialView): the route param only
  // picks which blocks show, so marked brands and the period carry across.
  const sub = useParams()['*'] ?? '';
  const view = (VIEWS as string[]).includes(sub) ? (sub as SocialView) : 'summary';
  if (sub && view === 'summary') return <Navigate to="/customer/market/social" replace />;
  return (
    <Suspense fallback={<Skeleton height={240} />}>
      <BrowseSocialAnalytics onBrowseRates={() => {}} view={view} />
    </Suspense>
  );
}

// A channel screen: its title and a way back to the channel choice.
function ChannelScreen({ k, children }: { k: 'digital' | 'outdoor'; children: React.ReactNode }) {
  const { t } = useTranslation('customer');
  const navigate = useNavigate();
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <button type="button" onClick={() => navigate('/customer/market')}
          style={{ display: 'inline-flex', alignItems: 'center', minHeight: 40, padding: '0 12px', borderRadius: 10, background: 'transparent',
            border: '1px solid rgba(255,255,255,0.15)', color: 'var(--accent-text)', cursor: 'pointer', fontFamily: 'inherit', fontSize: 'var(--fs-body)' }}>
          ← {t('market.title')}
        </button>
        <h1 style={{ margin: 0, fontSize: 'var(--fs-h1)', fontWeight: 'var(--fw-bold)' }}>{t(`market.${k}.title`)}</h1>
      </div>
      <Suspense fallback={<Skeleton height={240} />}>{children}</Suspense>
    </div>
  );
}

// Old /customer/browse?tab=… links (assistant, e-mails, bookmarks) land on the new screens,
// keeping the channel's own filters (?src/cat/sub/adv/active for Digital).
export function LegacyBrowseRedirect() {
  const { search } = useLocation();
  const sp = new URLSearchParams(search);
  let tab = sp.get('tab');
  if (!tab) { try { tab = localStorage.getItem('kgdb-browse-tab-v1'); } catch { /* ignore */ } }
  sp.delete('tab'); sp.delete('view');
  const rest = sp.toString() ? `?${sp.toString()}` : '';
  const to = tab === 'social' || tab === 'digital' || tab === 'outdoor' ? `/customer/market/${tab}${rest}` : '/customer/market';
  return <Navigate to={to} replace />;
}

export default function Market() {
  return (
    <Routes>
      <Route index element={<MarketHome />} />
      <Route path="social/*" element={<FeatureGate feature="an_social"><MarketSocial /></FeatureGate>} />
      <Route path="digital" element={<FeatureGate feature="an_digital"><ChannelScreen k="digital"><BrowseDigitalAnalytics /></ChannelScreen></FeatureGate>} />
      <Route path="outdoor" element={<FeatureGate feature="an_outdoor"><ChannelScreen k="outdoor"><BrowseOutdoorAnalytics onBrowseRates={() => {}} mode="map" /></ChannelScreen></FeatureGate>} />
      <Route path="outdoor/plan" element={<FeatureGate feature="an_outdoor"><Suspense fallback={<Skeleton height={240} />}><BrowseOutdoorAnalytics onBrowseRates={() => {}} mode="brief" /></Suspense></FeatureGate>} />
      <Route path="*" element={<Navigate to="/customer/market" replace />} />
    </Routes>
  );
}
